import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Table, Space, Spin, Row, Col, Statistic, Radio, Select, Button } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';

const { RangePicker } = DatePicker;

function Dashboard() {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateType, setDateType] = useState('order');
  const [dateRange, setDateRange] = useState([dayjs().subtract(6, 'day'), dayjs()]);
  const [platforms, setPlatforms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);

  useEffect(() => {
    loadOptions();
    loadData();
  }, []);

  const loadOptions = async () => {
    try {
      const [platRes, teamRes] = await Promise.all([
        api.get('/platforms'),
        api.get('/teams')
      ]);
      if (platRes.code === 200) setPlatforms(platRes.data?.list || platRes.data || []);
      if (teamRes.code === 200) setTeams(teamRes.data?.list || teamRes.data || []);
    } catch (err) {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { date_type: dateType };
      if (dateRange && dateRange[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange && dateRange[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      if (selectedPlatforms.length > 0) params.platform = selectedPlatforms.join(',');
      if (selectedTeams.length > 0) params.team = selectedTeams.join(',');

      const res = await api.get('/orders/stats', { params });
      if (res.code === 200) setData(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const STATUS_COLS = ['已发送授权', '未开抖加/搜不到', '涨粉中', '已涨粉', '需结账'];

  const dimColumns = (nameLabel) => [
    { title: nameLabel, dataIndex: 'name', ellipsis: true, width: 120 },
    { title: '订单数', dataIndex: 'count', width: 80, align: 'right' },
    ...STATUS_COLS.map(s => ({ title: s, dataIndex: s, width: 90, align: 'right', render: v => v || 0 }))
  ];

  const dimensions = [
    { key: 'supplier', label: '主体' },
    { key: 'team', label: '小组' },
    { key: 'handler', label: '受理员工' },
    { key: 'platform', label: '平台' },
    { key: 'growth_method', label: '涨粉方式' },
    { key: 'contact_person', label: '对接人' }
  ];

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userPlatforms = user.data_permissions?.platforms || [];
  const userTeams = user.data_permissions?.teams || [];

  // 根据数据权限过滤下拉选项（超管看全部，无配置则无权限）
  const filteredPlatforms = user.username === 'admin' ? platforms : platforms.filter(p => userPlatforms.includes(p.name));
  const filteredTeams = user.username === 'admin' ? teams : teams.filter(t => userTeams.includes(t.name));

  const filterStyle = isMobile ? { width: '100%', marginBottom: 8 } : { minWidth: 160 };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}><BarChartOutlined /> 数据看板</h3>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Radio.Group value={dateType} onChange={e => setDateType(e.target.value)}>
            <Radio.Button value="complete">按完成日期</Radio.Button>
            <Radio.Button value="order">按下单日期</Radio.Button>
          </Radio.Group>
          <RangePicker
            value={dateRange}
            onChange={v => setDateRange(v)}
            placeholder={['开始日期', '结束日期']}
            style={isMobile ? { width: '100%' } : {}}
          />
          <Select
            mode="multiple" allowClear placeholder="全部平台"
            value={selectedPlatforms} onChange={setSelectedPlatforms}
            maxTagCount={1} style={filterStyle}
            options={filteredPlatforms.map(p => ({ label: p.name, value: p.name }))}
          />
          <Select
            mode="multiple" allowClear placeholder="全部小组"
            value={selectedTeams} onChange={setSelectedTeams}
            maxTagCount={1} style={filterStyle}
            options={filteredTeams.map(t => ({ label: t.name, value: t.name }))}
          />
          <Button type="primary" onClick={loadData}>查询</Button>
          <Button onClick={() => { setDateRange(null); setSelectedPlatforms([]); setSelectedTeams([]); setTimeout(loadData, 100); }}>重置</Button>
        </div>
      </div>

      {loading ? (
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      ) : data ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 汇总卡片 */}
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Card><Statistic title="订单总数" value={data.total.count} /></Card>
            </Col>
            {(data.totalStatusStats || []).map(st => (
              <Col key={st.key} xs={24} sm={6}>
                <Card><Statistic title={st.key} value={st.count} /></Card>
              </Col>
            ))}
          </Row>

          {/* 各维度统计 */}
          {dimensions.map(dim => (
            <Card key={dim.key} title={`按${dim.label}统计`} size="small">
              <Table
                size="small"
                columns={dimColumns(dim.label)}
                dataSource={(data.stats[dim.key] || []).map((r, i) => ({ ...r, key: i }))}
                pagination={false}
                scroll={{ x: 800 }}
              />
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default Dashboard;
